import { httpClient } from 'providers/clients/httpClient';
import {
  buildCreateRequest,
  buildDeleteManyRequest,
  buildDeleteRequest,
  buildGetListRequest,
  buildGetManyReferenceRequest,
  buildGetManyRequest,
  buildGetOneRequest,
  buildPatchRequest,
  buildRequestBody,
  buildRequestUrl,
  buildUpdateManyRequest,
  buildUpdateRequest,
  getReactUniqueId,
  SomeRecord,
  TCreateParams,
  TCreateResult,
  TDeleteManyParams,
  TDeleteManyResult,
  TDeleteParams,
  TDeleteResult,
  TGetListParams,
  TGetListResult,
  TGetManyParams,
  TGetManyReferenceParams,
  TGetManyReferenceResult,
  TGetManyResult,
  TGetOneParams,
  TGetOneResult,
  TPatchParams,
  TPatchResult,
  TResourceTarget,
  TUpdateManyParams,
  TUpdateManyResult,
  TUpdateParams,
  TUpdateResult,
} from './functions/RequestBuilders';

// -----------------------------------------------
// --------------- RA DATA PROVIDER --------------
// -----------------------------------------------

/**
 * Type is meant to extend/override the react-admin
 * DataProvider type with our custom implementations.
 *
 * The custom type should have enough overlap with the
 * react-admin DataProvider type to be compatible.
 * interface BaseDataProvider extends DataProvider {...}
 *
 * It is better to create dedicated functions where possible,
 * rather than going through the ra data provider calls.
 */

export type TBaseCustomDataProvider = {
  getList: <RecordType extends SomeRecord = any>(
    resource: TResourceTarget,
    params: TGetListParams,
  ) => Promise<TGetListResult<RecordType>>;

  getOne: <RecordType extends SomeRecord = any>(
    resource: TResourceTarget,
    params: TGetOneParams,
  ) => Promise<TGetOneResult<RecordType>>;

  create: <ResultRecordType extends SomeRecord = any>(
    resource: TResourceTarget,
    params: TCreateParams,
  ) => Promise<TCreateResult<ResultRecordType>>;

  update: <RecordType extends SomeRecord = any>(
    resource: TResourceTarget,
    params: TUpdateParams,
  ) => Promise<TUpdateResult<RecordType>>;

  delete: <RecordType extends SomeRecord = any>(
    resource: TResourceTarget,
    params: TDeleteParams,
  ) => Promise<TDeleteResult<RecordType>>;

  getMany: <RecordType extends SomeRecord = any>(
    resource: TResourceTarget,
    params: TGetManyParams,
  ) => Promise<TGetManyResult<RecordType>>;

  getManyReference: <RecordType extends SomeRecord = any>(
    resource: TResourceTarget,
    params: TGetManyReferenceParams,
  ) => Promise<TGetManyReferenceResult<RecordType>>;

  deleteMany: <RecordType extends SomeRecord = any>(
    resource: TResourceTarget,
    params: TDeleteManyParams,
  ) => Promise<TDeleteManyResult<RecordType>>;

  updateMany: <RecordType extends SomeRecord = any>(
    resource: TResourceTarget,
    params: TUpdateManyParams,
  ) => Promise<TUpdateManyResult<RecordType>>;

  /**
   * Patch is a custom method, not in base ra.
   */
  patch: <RecordType extends SomeRecord = any>(
    resource: TResourceTarget,
    params: TPatchParams,
  ) => Promise<TPatchResult<RecordType>>;

  /**
   * Allows dataProvider.d.ts to be extended
   * without causing too many type errors.
   *
   * If you comment this line out you may see
   * some provider methods that have been missed
   * being defined on BloxCustomDataProvider type
   * over time, and should be properly defined there.
   */
  [key: string]: any;
};

export const baseCustomDataProvider = {
  getList: async (resource: TResourceTarget, params: TGetListParams) => {
    try {
      // Construct information for the request
      const { basePath, queryPayload, modifyGetListResponse } = buildGetListRequest({
        params: params,
        resource: resource,
      });

      const finalUrl = buildRequestUrl({ basePath, queryPayload });

      // Make the HTTP request
      const { data, status } = await httpClient({
        url: finalUrl,
        method: 'GET',
      });

      /**
       * Allow modification of response data before return.
       * Please use this carefully / sparingly for edge cases.
       * This should only be used if there is absolutely no other
       * way to transform the result data closer to the component
       * or area which is actually invoking / using the response.
       *
       * Currently just one case needs this:
       * "wire/channel/{id}/search/" as the api returns
       * "results" instead of "items" array in response.
       */
      if (typeof modifyGetListResponse === 'function') {
        // exit early with modified data
        const modifiedData = await modifyGetListResponse(data);
        return modifiedData;
      }

      // Map response data to standard format with IDs for RA
      const baseItems = data?.data?.items ?? [];
      const itemsArray = Array.isArray(baseItems) ? baseItems : [];

      // Get unique ID (for react keys, not neccessarily items id)
      const dataWithIds = itemsArray.map((item: any) => ({
        ...item,
        id: getReactUniqueId(item, resource),
      }));

      // Should return TGetListResult
      const formattedResult: TGetListResult = {
        status: data?.status ?? status,
        data: dataWithIds,
        total: data?.data?.total,
        pageInfo: {
          hasNextPage: Boolean(data?.data?.next),
          hasPreviousPage: Boolean(data?.data?.prev),
          nextPage: data?.data?.next,
          prevPage: data?.data?.prev,
        },
      };

      return formattedResult;
    } catch (error) {
      console.error('[getList]:', error);
      throw error;
    }
  },

  getOne: async (resource: TResourceTarget, params: TGetOneParams) => {
    try {
      const { basePath, queryPayload } = buildGetOneRequest({
        params: params,
        resource: resource,
      });

      const finalUrl = buildRequestUrl({ basePath, queryPayload });

      const { data, status } = await httpClient({
        url: finalUrl,
        method: 'GET',
      });

      // Should return TGetOneResult
      const formattedResult: TGetOneResult = {
        status: data?.status ?? status,
        data: {
          ...data,
          id: data?.data?.uuid,
        },
        id: data?.data?.uuid,
      };

      return formattedResult;
    } catch (error) {
      console.error('[getOne]:', error);
      throw error;
    }
  },

  create: async (resource: TResourceTarget, params: TCreateParams) => {
    try {
      const { basePath, bodyPayload, queryPayload } = buildCreateRequest({
        params: params,
        resource: resource,
      });

      // Build query string (rare, if any)
      const finalUrl = buildRequestUrl({ basePath, queryPayload });

      // Stringify body payload (should exist)
      const finalBody = buildRequestBody({ payload: bodyPayload });

      const { data, status } = await httpClient({
        url: finalUrl,
        method: 'POST',
        data: finalBody,
      });

      // Should return TCreateResult
      const formattedResult: TCreateResult = {
        status: data?.status ?? status,
        data: {
          ...data,
          id: data?.data?.uuid,
        },
        id: data?.data?.uuid,
      };

      return formattedResult;
    } catch (error) {
      console.error('[create]:', error);
      throw error;
    }
  },

  update: async (resource: TResourceTarget, params: TUpdateParams) => {
    try {
      const { basePath, bodyPayload, queryPayload } = buildUpdateRequest({
        params: params,
        resource: resource,
      });

      // Build query string (rare, if any)
      const finalUrl = buildRequestUrl({ basePath, queryPayload });

      // Stringify body payload (should exist)
      const finalBody = buildRequestBody({ payload: bodyPayload });

      const { data, status } = await httpClient({
        url: finalUrl,
        method: 'PUT',
        data: finalBody,
      });

      // Should return TUpdateResult
      const formattedResult: TUpdateResult = {
        status: data?.status ?? status,
        data: { ...data, id: data?.data?.uuid },
        id: data?.data?.uuid,
      };

      return formattedResult;
    } catch (error) {
      console.error('[update]:', error);
      throw error;
    }
  },

  delete: async (resource: TResourceTarget, params: TDeleteParams) => {
    try {
      const { basePath, queryPayload } = buildDeleteRequest({
        params: params,
        resource: resource,
      });

      // Build final query string
      const finalUrl = buildRequestUrl({ basePath, queryPayload });

      const { data, status } = await httpClient({
        url: finalUrl,
        method: 'DELETE',
      });

      // Should return TDeleteResult
      const formattedResult: TDeleteResult = {
        status: data?.status ?? status,
        data: { ...data },
      };

      return formattedResult;
    } catch (error) {
      console.error('[delete]:', error);
      throw error;
    }
  },

  /**
   * Patch is a custom method added to the base RA dataProvider
   */
  patch: async (resource: TResourceTarget, params: TPatchParams) => {
    try {
      const { basePath, queryPayload, patchArray } = buildPatchRequest({
        params: params,
        resource: resource,
      });

      // Build query string
      const finalUrl = buildRequestUrl({ basePath, queryPayload });
      // Patch stringify the array of operations directly
      const finalBody = buildRequestBody({ payload: patchArray });

      const { data, status } = await httpClient({
        url: finalUrl,
        method: 'PATCH',
        data: finalBody,
      });

      // Should return TPatchResult
      const formattedResult: TPatchResult = {
        status: data?.status ?? status,
        data: { ...data, id: data?.data?.uuid },
        id: data?.data?.uuid,
      };

      return formattedResult;
    } catch (error) {
      console.error('[patch]:', error);
      throw error;
    }
  },

  /**
   * The following methods are unused or not fully implemented:
   * getMany, getManyReference updateMany, deleteMany.
   *
   * They are stubbed in here to satisfy the RA DataProvider type.
   * Generally, the way RA defines these methods is not very
   * compatible with how our API endpoints are structured.
   * We genererally instead create proper functions per endpoint
   * in their respective extension files where it is possible.
   * eg: extensions\Editorial.tsx, extensions\Broadcast.tsx, etc.
   * which makes it easier to debug/trace calls and data flow.
   *
   */

  deleteMany: async (resource: TResourceTarget, params: TDeleteManyParams) => {
    try {
      const { basePath, queryPayload, bodyPayload } = buildDeleteManyRequest({
        params: params,
        resource: resource,
      });

      // Build query string
      const finalUrl = buildRequestUrl({ basePath, queryPayload });
      // Stringify body payload (should exist)
      const finalBody = buildRequestBody({ payload: bodyPayload });

      const { data, status } = await httpClient({
        url: finalUrl,
        method: 'POST',
        data: finalBody,
      });

      // Should return TDeleteResult
      const formattedResult: TDeleteManyResult = {
        status: data?.status ?? status,
        data: data?.data?.items ?? [],
      };

      return formattedResult;
    } catch (error) {
      console.error('[deleteMany]:', error);
      throw error;
    }
  },

  getMany: async (resource: TResourceTarget, params: TGetManyParams) => {
    try {
      const { basePath, queryPayload, bodyPayload } = buildGetManyRequest({
        params: params,
        resource: resource,
      });

      // Build query string
      const finalUrl = buildRequestUrl({ basePath, queryPayload });

      // Stringify body payload (if exists)
      const finalBody = buildRequestBody({ payload: bodyPayload });

      const { data, status } = await httpClient({
        url: finalUrl,
        method: 'GET',
        data: finalBody,
      });

      // Should return TGetManyResult
      const itemsArray = data?.data?.items ?? [];

      const formattedResult: TGetManyResult = {
        status: data?.status ?? status,
        data: itemsArray,
      };

      return formattedResult;
    } catch (error) {
      console.error('[getMany]:', error);
      throw error;
    }
  },

  getManyReference: async (
    resource: TResourceTarget,
    params: TGetManyReferenceParams,
  ) => {
    try {
      const { basePath, queryPayload, bodyPayload } = buildGetManyReferenceRequest({
        params: params,
        resource: resource,
      });

      // Build query string
      const finalUrl = buildRequestUrl({ basePath, queryPayload });

      // Stringify body payload (if exists)
      const finalBody = buildRequestBody({ payload: bodyPayload });

      const { data, status } = await httpClient({
        url: finalUrl,
        method: 'GET',
        data: finalBody,
      });

      // Map response data to standard format with IDs for RA
      const itemsArray = data?.data?.items ?? [];

      const formattedResult: TGetManyReferenceResult = {
        status: data?.status ?? status,
        data: itemsArray,
        total: data?.data?.total,
        pageInfo: {
          hasNextPage: Boolean(data?.data?.next),
          hasPreviousPage: Boolean(data?.data?.prev),
        },
      };

      return formattedResult;
    } catch (error) {
      console.error('[getManyReference]:', error);
      throw error;
    }
  },

  updateMany: async (resource: TResourceTarget, params: TUpdateManyParams) => {
    try {
      const { basePath, queryPayload, bodyPayload } = buildUpdateManyRequest({
        params: params,
        resource: resource,
      });

      // Build query string
      const finalUrl = buildRequestUrl({ basePath, queryPayload });
      // Stringify body payload (should exist)
      const finalBody = buildRequestBody({ payload: bodyPayload });

      const { data, status } = await httpClient({
        url: finalUrl,
        method: 'PUT',
        data: finalBody,
      });

      // IDs array from response data (per ra types)
      // Should return TUpdateManyResult
      const formattedResult: TUpdateManyResult = {
        status: data?.status ?? status,
        data: data?.data?.items ?? [],
      };

      return formattedResult;
    } catch (error) {
      console.error('[updateMany]:', error);
      throw error;
    }
  },

  // set as const - all keys (functions) in this object
  // are readonly and we should to ensure they
  // are immutable / cant be accidentally modified
} as const;
